/**
 * The holes of Tacada Limpa, in table pixels (640 x 1000, origin top-left, y down). `holeCourse` converts to physics units.
 * Each hole has cup variants: the seed picks one, so a hole is not a single memorisable shot (criteria map test).
 */
import { SUB, type Course, type Diag, type Gate, type Pt, type Rect, type Zone } from './physics'

export interface HoleDef {
  id: string
  name: string
  par: number
  tee: Pt
  cups: Pt[]
  walls: Rect[]
  diags: Diag[]
  zones: Zone[]
  gates: Gate[]
}

const r = (x: number, y: number, w: number, h: number): Rect => ({ x, y, w, h })

export const HOLES: Record<string, HoleDef> = {
  canteiro: {
    id: 'canteiro', name: 'Canteiro', par: 2, tee: { x: 320, y: 900 },
    cups: [{ x: 320, y: 150 }, { x: 170, y: 140 }, { x: 470, y: 140 }],
    walls: [r(230, 430, 180, 70)], diags: [], zones: [], gates: []
  },
  cotovelo: {
    id: 'cotovelo', name: 'Cotovelo', par: 3, tee: { x: 120, y: 900 },
    cups: [{ x: 110, y: 140 }, { x: 200, y: 120 }, { x: 90, y: 230 }],
    walls: [r(0, 300, 430, 260)], diags: [], zones: [], gates: []
  },
  diagonal: {
    id: 'diagonal', name: 'Diagonal', par: 2, tee: { x: 500, y: 900 },
    cups: [{ x: 120, y: 140 }, { x: 320, y: 120 }, { x: 520, y: 160 }],
    walls: [],
    diags: [{ kind: '/', c: 700, lo: -500, hi: -100 }, { kind: '\\', c: 40, lo: 640, hi: 1040 }],
    zones: [{ kind: 'sand', ...r(380, 600, 180, 120) }], gates: []
  },
  funil: {
    id: 'funil', name: 'Funil', par: 3, tee: { x: 320, y: 900 },
    cups: [{ x: 320, y: 140 }, { x: 150, y: 200 }, { x: 490, y: 200 }],
    walls: [r(250, 250, 140, 40)],
    diags: [{ kind: '\\', c: -340, lo: 420, hi: 860 }, { kind: '/', c: 980, lo: -220, hi: 220 }],
    zones: [], gates: []
  },
  portao: {
    id: 'portao', name: 'Portão', par: 3, tee: { x: 320, y: 900 },
    cups: [{ x: 320, y: 140 }, { x: 500, y: 180 }, { x: 140, y: 130 }],
    walls: [r(0, 480, 260, 40), r(380, 480, 260, 40)], diags: [],
    zones: [{ kind: 'sand', ...r(60, 200, 200, 120) }],
    gates: [{ ...r(260, 470, 120, 60), axis: 'x', amp: 130, periodMs: 2400 }]
  },
  rampa: {
    id: 'rampa', name: 'Rampa', par: 3, tee: { x: 100, y: 900 },
    cups: [{ x: 560, y: 140 }, { x: 420, y: 120 }, { x: 600, y: 260 }],
    walls: [r(380, 650, 260, 60)], diags: [],
    zones: [{ kind: 'ramp', ...r(0, 350, 640, 250), dx: -3, dy: 0 }], gates: []
  }
}

/** Holes a round of each level draws from (the seed picks 3 and their order). */
export const LEVEL_POOLS: Record<string, string[]> = {
  '1': ['canteiro', 'cotovelo', 'diagonal'],
  '2': ['cotovelo', 'diagonal', 'funil', 'rampa'],
  '3': ['funil', 'portao', 'rampa']
}

const sub = (p: Pt): Pt => ({ x: p.x * SUB, y: p.y * SUB })
const subRect = <T extends Rect>(o: T): T => ({ ...o, x: o.x * SUB, y: o.y * SUB, w: o.w * SUB, h: o.h * SUB })

/** Physics course of a hole with the chosen cup variant and gate phase. */
export function holeCourse(h: HoleDef, cupVariant: number, gatePhaseMs: number): Course {
  return {
    tee: sub(h.tee),
    cup: sub(h.cups[cupVariant % h.cups.length]),
    walls: h.walls.map(subRect),
    diags: h.diags.map((d) => ({ ...d, c: d.c * SUB, lo: d.lo * SUB, hi: d.hi * SUB })),
    zones: h.zones.map((z) => ({ ...subRect(z), dx: (z.dx ?? 0) * SUB, dy: (z.dy ?? 0) * SUB })),
    gates: h.gates.map((g) => ({ ...subRect(g), amp: g.amp * SUB })),
    gatePhaseMs
  }
}
